import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";


import { toast } from "sonner";
import { Copy } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export const CopyButton = ({ text, testId, label }) => {
  const { t } = useLang();
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(t("copied"));
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text || "";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(t("copied"));
    }
  };
  return (
    <button
      data-testid={testId || "copy-button"}
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-500 hover:text-stone-900 border border-stone-200 hover:border-stone-300 rounded-lg px-3 py-1.5 transition-colors bg-white"
    >
      <Copy size={13} strokeWidth={1.5} />
      {label || t("copy")}
    </button>
  );
};

export const PageHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
    <div>
      <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-stone-900">{title}</h1>
      {subtitle && <p className="text-sm text-stone-500 mt-1.5">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className="text-xs uppercase tracking-[0.12em] font-semibold text-stone-500">{label}</label>
    {children}
  </div>
);

export const EmptyState = ({ message }) => (
  <div className="text-center py-16 text-stone-400 text-sm border border-dashed border-stone-200 rounded-xl bg-white">
    {message}
  </div>
);

export const inputCls =
  "w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent placeholder:text-stone-400";

// Immutable, human-readable ID badge (B-0001, S-0001, P-0001, etc.)
export const RefBadge = ({ code, size = "sm", tone = "default" }) => {
  if (!code) return null;
  const sizeCls = size === "md"
    ? "px-2.5 py-1 text-xs"
    : "px-2 py-0.5 text-[11px]";
  const toneCls = tone === "quiet"
    ? "bg-transparent text-stone-400 border-stone-200"
    : "bg-stone-100 text-stone-600 border-stone-200";
  return (
    <span
      data-testid={`ref-code-${code}`}
      className={`inline-flex items-center rounded-md border font-mono tracking-wide ${sizeCls} ${toneCls}`}
    >
      {code}
    </span>
  );
};

// Prepend the ref code to a display label if present ("B-0001 · Name").
export const withRef = (code, label) => (code ? `${code} · ${label || ""}` : (label || ""));

// Split a stored full name into first name / surname (last word = surname).
export const splitName = (record) => {
  if (record.first_name || record.last_name)
    return { first_name: record.first_name || "", last_name: record.last_name || "" };
  const parts = (record.name || "").trim().split(" ");
  return parts.length > 1
    ? { first_name: parts.slice(0, -1).join(" "), last_name: parts[parts.length - 1] }
    : { first_name: "", last_name: record.name || "" };
};

// Sortable table header cell. Click cycles the sort direction on that column.
// Icons show current sort state so Julia can always tell which column drives the order.
export const SortableTh = ({ children, onClick, active, dir, align = "left" }) => {
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  const alignCls = align === "right" ? "text-right justify-end" : "text-left justify-start";
  return (
    <th className="py-2.5 px-3 font-medium">
      <button
        type="button"
        onClick={onClick}
        className={`w-full inline-flex items-center gap-1.5 uppercase tracking-wider text-[11px] text-stone-500 hover:text-stone-900 transition-colors ${alignCls}`}
      >
        {children}
        <Icon size={11} strokeWidth={1.75} className={active ? "text-stone-900" : "text-stone-300"} />
      </button>
    </th>
  );
};
